import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams, useBranding } from '../../hooks';
import { utils, Types as coreTypes } from '@ohif/core';

import {
  StudyListExpandedRow,
  EmptyStudies,
  StudyListTable,
  StudyListFilter,
  Button,
  ButtonEnums,
} from '@ohif/ui';

import {
  Header,
  Icons,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Clipboard,
  useModal,
  useSessionStorage,
  Onboarding,
  ScrollArea,
} from '@ohif/ui-next';

import { Types } from '@ohif/ui';

import { preserveQueryParameters, preserveQueryStrings } from '../../utils/preserveQueryParameters';

const PatientInfoVisibility = Types.PatientInfoVisibility;

const { sortBySeriesDate } = utils;

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
}: withAppTypes) {
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Branding
  const branding = useBranding();
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 101;

  // Get and store patient parameters and JWT from URL, then remove them
  const [storedPatientData, setStoredPatientData] = useSessionStorage({
    key: 'patientFilterData',
    defaultValue: { patientName: null, patientBirthDate: null, radJwt: null },
    clearOnUnload: false,
  });

  // Extract and store parameters on first load, then remove from URL
  useEffect(() => {
    const patientName = searchParams.get('patientname') || searchParams.get('patientName');
    const patientBirthDate = searchParams.get('patientbirthdate') || searchParams.get('patientBirthDate') || searchParams.get('dob');
    const radJwt = searchParams.get('radjwt') || searchParams.get('radJwt') || searchParams.get('jwt');

    // If parameters are in URL and BOTH patient parameters are provided, store them and remove from URL
    if (patientName && patientBirthDate) {
      // Normalize patient name to remove trailing carets and extra whitespace
      // DICOM names use ^ as separator, but trailing empty components should be removed
      // e.g., "Doe^John^^" should become "Doe^John"
      const normalizedPatientName = patientName
        .replace(/\^+$/, '')  // Remove trailing carets
        .trim();              // Remove leading/trailing whitespace

      console.log('Storing patient parameters and JWT in session and removing from URL:', {
        patientName: patientName,
        normalizedPatientName: normalizedPatientName,
        patientBirthDate,
        hasRadJwt: !!radJwt,
      });

      setStoredPatientData({
        patientName: normalizedPatientName,
        patientBirthDate: patientBirthDate,
        // captureRadJwt() has usually already moved the token out of the URL
        radJwt: radJwt || storedPatientData.radJwt || null,
      });

      // Verify sessionStorage actually worked before removing URL params
      // Some browsers (Safari ITP, Firefox ETP, Brave) block storage in cross-origin iframes
      let storageWorked = false;
      try {
        const storedData = sessionStorage.getItem('patientFilterData');
        if (storedData) {
          const parsed = JSON.parse(storedData);
          storageWorked = parsed.patientName === normalizedPatientName &&
                          parsed.patientBirthDate === patientBirthDate;
        }
      } catch (e) {
        console.warn('sessionStorage not available in this context (likely cross-origin iframe):', e);
      }

      // Only remove params from URL if storage worked, otherwise keep them for fallback
      if (storageWorked) {
        const newSearchParams = new URLSearchParams(window.location.search);
        newSearchParams.delete('patientname');
        newSearchParams.delete('patientName');
        newSearchParams.delete('patientbirthdate');
        newSearchParams.delete('patientBirthDate');
        newSearchParams.delete('dob');
        newSearchParams.delete('radjwt');
        newSearchParams.delete('radJwt');
        newSearchParams.delete('jwt');

        const newSearch = newSearchParams.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;

        // Replace URL without triggering navigation
        window.history.replaceState({}, '', newUrl);

        // Trigger a navigation to clean URL to ensure DataSourceWrapper sees the change
        navigate(newSearch ? `/?${newSearch}` : '/', { replace: true });
      } else {
        console.log('Keeping params in URL as sessionStorage fallback is not available');
      }
    }
  }, []); // Run only once on mount

  // Use stored values for filtering, with URL params as fallback for browsers that block sessionStorage
  const urlPatientName = searchParams.get('patientname') || searchParams.get('patientName');
  const urlPatientBirthDate = searchParams.get('patientbirthdate') || searchParams.get('patientBirthDate') || searchParams.get('dob');
  const effectivePatientName = storedPatientData.patientName || urlPatientName;
  const effectivePatientBirthDate = storedPatientData.patientBirthDate || urlPatientBirthDate;

  // Validate required parameters
  const [hasValidatedParams, setHasValidatedParams] = useState(false);
  useEffect(() => {
    if (!effectivePatientName || !effectivePatientBirthDate) {
      servicesManager.services.uiModalService.show({
        title: 'Missing Required Parameters',
        content: () => (
          <div className="text-foreground">
            <p className="text-red-600">Error: Required patient parameters are missing.</p>
            <p className="mt-2">
              This viewer must be accessed with both 'patientname' and 'patientbirthdate' (or 'dob') query parameters.
            </p>
            <p className="mt-2">
              Example: ?patientname=John^Doe&patientbirthdate=19800101
            </p>
            <p className="mt-2 text-sm text-gray-400">
              Alternative: ?patientname=John^Doe&dob=1980-01-01
            </p>
          </div>
        ),
      });
      setHasValidatedParams(false);
    } else {
      setHasValidatedParams(true);
    }
  }, [effectivePatientName, effectivePatientBirthDate]);

  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
    // so on systems that do not support unload events a user will NOT be able to alter any existing filter
    // in the URL, load the page and have it apply.
    clearOnUnload: true,
  });
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};
  const { customizationService } = servicesManager.services;

  // Client-side filtering for exact match on patientName and patientBirthDate
  // Backend may not support filtering by birth date, so we filter here
  const filteredStudies = useMemo(() => {
    console.log('=== CLIENT-SIDE FILTERING ===');
    console.log('Total studies from backend:', studies.length);
    console.log('Filter patient name (raw):', effectivePatientName);
    console.log('Filter patient birth date (raw):', effectivePatientBirthDate);

    if (!effectivePatientName || !effectivePatientBirthDate) {
      console.log('No filter parameters, returning all studies');
      return studies;
    }

    // Normalize patient name for comparison (case-insensitive, handles DICOM format)
    // DICOM names use ^ as separator: FamilyName^GivenName^MiddleName^Prefix^Suffix
    // Empty components result in consecutive carets (e.g., "Doe^John^^" has empty middle name and suffix)
    const normalizePatientName = (name) => {
      if (!name) return '';

      let normalized = name.toUpperCase().trim();

      // Handle DICOM format: FamilyName^GivenName^MiddleName^Prefix^Suffix
      if (normalized.includes('^')) {
        const parts = normalized.split('^');
        // Keep only first 3 components (family, given, middle) - ignore titles/prefixes
        normalized = parts.slice(0, 3).join('^');
      }

      // Normalize to space-separated words for comparison
      return normalized
        .replace(/\^/g, ' ')    // Convert carets to spaces
        .replace(/,+/g, ' ')    // Convert commas to spaces
        .replace(/\s+/g, ' ')   // Normalize multiple spaces to single space
        .trim();                // Remove leading/trailing whitespace
    };

    const normalizedFilterName = normalizePatientName(effectivePatientName);
    console.log('Filter patient name (normalized):', normalizedFilterName);

    const filtered = studies.filter(study => {
      const normalizedStudyName = normalizePatientName(study.patientName || '');

      // Use contains match for flexibility (handles partial matches)
      const nameMatches = normalizedStudyName.includes(normalizedFilterName) ||
                         normalizedFilterName.includes(normalizedStudyName);

      // Normalize birth date for exact comparison (remove all non-digits)
      const studyBirthDate = (study.patientBirthDate || '')
        .toString()
        .replace(/\D/g, '');  // Remove all non-digit characters
      const filterBirthDate = (effectivePatientBirthDate || '')
        .toString()
        .replace(/\D/g, '');  // Remove all non-digit characters
      const birthDateMatches = studyBirthDate === filterBirthDate;

      const passes = nameMatches && birthDateMatches;

      if (!passes) {
        console.log('Study filtered out:', {
          rawStudyName: study.patientName,
          normalizedStudyName,
          normalizedFilterName,
          rawStudyBirthDate: study.patientBirthDate,
          studyBirthDate,
          filterBirthDate,
          nameMatches,
          birthDateMatches,
        });
      }

      return passes;
    });

    console.log(`Filtered ${studies.length} studies down to ${filtered.length}`);
    return filtered;
  }, [studies, effectivePatientName, effectivePatientBirthDate]);

  const sortedStudies = useMemo(() => {
    if (!canSort) {
      return filteredStudies;
    }

    return [...filteredStudies].sort((s1, s2) => {
      if (shouldUseDefaultSort) {
        const ascendingSortModifier = -1;
        return _sortStringDates(s1, s2, ascendingSortModifier);
      }

      const s1Prop = s1[sortBy];
      const s2Prop = s2[sortBy];

      if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
        return s1Prop.localeCompare(s2Prop) * sortModifier;
      } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
        return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
      } else if (!s1Prop && s2Prop) {
        return -1 * sortModifier;
      } else if (!s2Prop && s1Prop) {
        return 1 * sortModifier;
      } else if (sortBy === 'studyDate') {
        return _sortStringDates(s1, s2, sortModifier);
      }

      return 0;
    });
  }, [canSort, filteredStudies, shouldUseDefaultSort, sortBy, sortModifier]);

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  // Use filtered count to reflect client-side filtering
  const numOfStudies = filteredStudies.length;
  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows]);

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
  };

  const onPageNumberChange = newPageNumber => {
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage = Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters
  // NOTE: Patient parameters are NOT in URL, they're stored in session storage
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString = {};

    // DO NOT include patient parameters in URL - they're locked in session storage

    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    preserveQueryStrings(queryString);

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });
    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(101 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  const offset = resultsPerPage * rollingPageNumber;
  const offsetAndTake = offset + resultsPerPage;
  const tableDataSource = sortedStudies.map((study, key) => {
    const rowKey = key + 1;
    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      studyInstanceUid,
      accession,
      modalities,
      instances,
      description,
      mrn,
      date,
      time,
    } = study;
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'));
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
        t('Common:localTimeFormat', 'hh:mm A')
      );

    const makeCopyTooltipCell = textValue => {
      if (!textValue) {
        return '';
      }
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-pointer truncate">{textValue}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center justify-between gap-2">
              {textValue}
              <Clipboard>{textValue}</Clipboard>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    };

    return {
      dataCY: `studyRow-${studyInstanceUid}`,
      clickableCY: studyInstanceUid,
      row: [
        {
          key: 'mrn',
          content: makeCopyTooltipCell(mrn),
          gridCol: 3,
        },
        {
          key: 'studyDate',
          content: (
            <>
              {studyDate && <span className="mr-4">{studyDate}</span>}
              {studyTime && <span>{studyTime}</span>}
            </>
          ),
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 6,
        },
        {
          key: 'description',
          content: makeCopyTooltipCell(description),
          gridCol: 5,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 3,
        },
        {
          key: 'accession',
          content: makeCopyTooltipCell(accession),
          gridCol: 3,
        },
        {
          key: 'instances',
          content: (
            <>
              <Icons.GroupLayers
                className={classnames('mr-2 inline-flex w-4', {
                  'text-primary': isExpanded,
                  'text-secondary-light': !isExpanded,
                })}
              />
              {instances}
            </>
          ),
          title: (instances || 0).toString(),
          gridCol: 2,
        },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      expandedContent: (
        <StudyListExpandedRow
          seriesTableColumns={{
            description: t('StudyList:Description'),
            seriesNumber: t('StudyList:Series'),
            modality: t('StudyList:Modality'),
            instances: t('StudyList:Instances'),
          }}
          seriesTableDataSource={
            seriesInStudiesMap.has(studyInstanceUid)
              ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
                  return {
                    description: s.description || '(empty)',
                    seriesNumber: s.seriesNumber ?? '',
                    modality: s.modality || '',
                    instances: s.numSeriesInstances || '',
                  };
                })
              : []
          }
        >
          <div className="flex flex-row gap-2">
            {(appConfig.groupEnabledModesFirst
              ? appConfig.loadedModes.sort((a, b) => {
                  const isValidA = a.isValidMode({
                    modalities: modalities.replaceAll('/', '\\'),
                    study,
                  }).valid;
                  const isValidB = b.isValidMode({
                    modalities: modalities.replaceAll('/', '\\'),
                    study,
                  }).valid;

                  return isValidB - isValidA;
                })
              : appConfig.loadedModes
            ).map((mode, i) => {
              if (mode.hide) {
                // Hide this mode from display
                return null;
              }
              const modalitiesToCheck = modalities.replaceAll('/', '\\');

              const { valid: isValidMode, description: invalidModeDescription } = mode.isValidMode({
                modalities: modalitiesToCheck,
                study,
              });
              if (isValidMode === null) {
                // Hide this as a computed result.
                return null;
              }

              // TODO: Modes need a default/target route? We mostly support a single one for now.
              // We should also be using the route path, but currently are not
              // mode.routeName
              // mode.routes[x].path
              // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
              // How do we know which params to pass? Today, it's just StudyInstanceUIDs and configUrl if exists
              const query = new URLSearchParams();
              if (filterValues.configUrl) {
                query.append('configUrl', filterValues.configUrl);
              }
              query.append('StudyInstanceUIDs', studyInstanceUid);
              // Marker so the viewer header can show the "Index" return button
              // only when launched from the worklist, not from a direct
              // /viewer?StudyInstanceUIDs=... URL (e.g. grouped-view "View"
              // action). sessionStorage alone is unreliable because it
              // persists across iframe loads in the same tab.
              query.append('fromWorklist', '1');
              preserveQueryParameters(query);

              return (
                mode.displayName && (
                  <Link
                    className={isValidMode ? '' : 'cursor-not-allowed'}
                    key={i}
                    to={`${mode.routeName}${dataPath || ''}?${query.toString()}`}
                    onClick={event => {
                      // In case any event bubbles up for an invalid mode, prevent the navigation.
                      // For example, the event bubbles up when the icon embedded in the disabled button is clicked.
                      if (!isValidMode) {
                        event.preventDefault();
                      }
                    }}
                    // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
                  >
                    {/* TODO revisit the completely rounded style of buttons used for launching a mode from the worklist later */}
                    <Button
                      type={ButtonEnums.type.primary}
                      size="small"
                      disabled={!isValidMode}
                      startIconTooltip={
                        !isValidMode ? (
                          <div className="font-inter flex w-[206px] whitespace-normal text-left text-xs font-normal text-white">
                            {invalidModeDescription}
                          </div>
                        ) : null
                      }
                      startIcon={
                        isValidMode ? (
                          <Icons.LaunchArrow className="!h-[20px] !w-[20px] text-black" />
                        ) : (
                          <Icons.LaunchInfo className="!h-[20px] !w-[20px] text-black" />
                        )
                      }
                      onClick={() => {}}
                      dataCY={`mode-${mode.routeName}-${studyInstanceUid}`}
                      className={!isValidMode ? 'bg-[#222d44]' : ''}
                    >
                      {mode.displayName}
                    </Button>
                  </Link>
                )
              );
            })}
          </div>
        </StudyListExpandedRow>
      ),
      onClickRow: () =>
        setExpandedRows(s => (isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey])),
      isExpanded,
    };
  });

  const hasStudies = numOfStudies > 0;

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );
  const DicomUploadComponent = customizationService.getCustomization('dicomUploadComponent');

  const uploadProps =
    DicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled
      ? {
          title: 'Upload files',
          containerClassName: DicomUploadComponent?.containerClassName,
          closeButton: true,
          shouldCloseOnEsc: false,
          shouldCloseOnOverlayClick: false,
          content: () => (
            <DicomUploadComponent
              dataSource={dataSource}
              onComplete={() => {
                hide();
                onRefresh();
              }}
              onStarted={() => {
                show({
                  ...uploadProps,
                  // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
                  closeButton: false,
                });
              }}
            />
          ),
        }
      : undefined;

  const dataSourceConfigurationComponent = customizationService.getCustomization(
    'ohif.dataSourceConfigurationComponent'
  );

  // Create custom WhiteLabeling with domain-specific branding
  const customWhiteLabeling = {
    ...appConfig.whiteLabeling,
    createLogoComponentFn: (React) => {
      return (
        <div className="flex items-center gap-2">
          <img
            src={branding.logo}
            alt={branding.name}
            className="h-8"
            onError={(e) => {
              // Fallback to default OHIF logo if custom logo fails to load
              console.warn(`Failed to load logo: ${branding.logo}, falling back to default`);
              e.currentTarget.src = '/ohif-logo.svg';
            }}
          />
          {branding.showTitle !== false && (
            <span className="text-primary-light text-lg font-medium">{branding.name}</span>
          )}
        </div>
      );
    },
  };

  return (
    <div className="flex h-screen flex-col bg-black">
      <Header
        isSticky
        menuOptions={[]}
        isReturnEnabled={false}
        WhiteLabeling={customWhiteLabeling}
        showPatientInfo={PatientInfoVisibility.DISABLED}
      />
      <Onboarding />
      {!hasValidatedParams ? (
        <div className="flex h-full flex-col items-center justify-center">
          <div className="text-foreground max-w-lg rounded-lg border border-red-600 bg-black p-8 text-center">
            <p className="mb-4 text-xl font-bold text-red-600">Missing Required Parameters</p>
            <p className="mb-2">
              This viewer must be accessed with both 'patientname' and 'patientbirthdate' (or 'dob') query
              parameters.
            </p>
            <p className="mt-4 font-mono text-sm text-gray-400">
              Example: ?patientname=John^Doe&patientbirthdate=19800101
            </p>
            <p className="mt-2 font-mono text-sm text-gray-400">
              Alternative: ?patientname=John^Doe&dob=1980-01-01
            </p>
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col overflow-y-auto">
          <ScrollArea>
            <div className="flex grow flex-col">
              <StudyListFilter
                numOfStudies={numOfStudies}
                filtersMeta={filtersMeta}
                filterValues={{ ...filterValues, ...defaultSortValues }}
                onChange={setFilterValues}
                clearFilters={() => setFilterValues(defaultFilterValues)}
                isFiltering={isFiltering(filterValues, defaultFilterValues)}
                onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
                getDataSourceConfigurationComponent={
                  dataSourceConfigurationComponent
                    ? () => dataSourceConfigurationComponent()
                    : undefined
                }
              />
            </div>
            {hasStudies ? (
              <div className="flex grow flex-col">
                <StudyListTable
                  tableDataSource={tableDataSource}
                  numOfStudies={numOfStudies}
                  querying={querying}
                  filtersMeta={filtersMeta}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center pt-48">
                {appConfig.showLoadingIndicator && isLoadingData ? (
                  <LoadingIndicatorProgress className={'h-full w-full bg-black'} />
                ) : (
                  <EmptyStudies />
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 10000, // Set high limit to get all results (no pagination)
  datasources: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const newParams = new URLSearchParams();
  for (const [key, value] of params) {
    newParams.set(key.toLowerCase(), value);
  }
  params = newParams;

  const queryFilterValues = {
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    description: params.get('description'),
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
